import type { TFunction } from 'i18next';
import {
  formatItalianAmount,
  type AmountRangeValue,
} from '@repo/ui/components/molecules/filter-panel';
import { formatDateString } from '@/lib/helpers/dateUtilities';
import {
  normalizeAmountRange,
  toPeriodParams,
  type PanelDateRange,
} from './panel-params';

/**
 * Testi delle pill della riga filtri condivisi da tutte le tabelle col
 * pannello Filtri (chiavi `commons.filter_panel_chips.*`): ogni lista mette
 * la propria etichetta, il valore si legge ovunque allo stesso modo.
 */

/**
 * Valore della pill del periodo: «01/09/2026 – 30/09/2026», oppure «dal …» /
 * «fino al …» per un periodo a metà. Da chiamare solo con `hasPeriod`: il
 * periodo vuoto non ha pill. Il trattino lungo è quello che il toolbar
 * riconosce come periodo completo.
 */
export function formatPeriodChipValue(
  value: PanelDateRange | null | undefined,
  t: TFunction
): string {
  const { from, to } = toPeriodParams(value);
  const start = from ? formatDateString(from) : null;
  const end = to ? formatDateString(to) : null;
  if (start && end) return `${start} – ${end}`;
  if (start)
    return t('commons.filter_panel_chips.period_from', { date: start });
  return t('commons.filter_panel_chips.period_to', { date: end ?? '' });
}

/**
 * Valore della pill «Importo»: "10 – 500 €", "da 10 €", "fino a 500 €";
 * `null` senza estremi (nessuna pill). Gli importi usano la stessa grafia del
 * campo del pannello, così la pill si legge come il campo.
 */
export function formatAmountRangeChip(
  range: AmountRangeValue | null | undefined,
  t: TFunction
): string | null {
  const normalized = normalizeAmountRange(range);
  const min = normalized?.min;
  const max = normalized?.max;
  if (min !== undefined && max !== undefined) {
    return t('commons.filter_panel_chips.amount_range', {
      min: formatItalianAmount(min),
      max: formatItalianAmount(max),
    });
  }
  if (min !== undefined) {
    return t('commons.filter_panel_chips.amount_from', {
      amount: formatItalianAmount(min),
    });
  }
  if (max !== undefined) {
    return t('commons.filter_panel_chips.amount_to', {
      amount: formatItalianAmount(max),
    });
  }
  return null;
}
